import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { usePOSConfig } from '@/api/config'
import { useSessionStore } from '@/stores/sessionStore'

const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'touchstart',
  'pointerdown',
  'wheel',
  'scroll',
] as const

/**
 * After `pos.logout_timeout` seconds with no user activity, drop the
 * clerk auth and route to `/login` (PIN view). The session token is
 * preserved so the clerk just re-enters their PIN — no full logout.
 *
 * Disabled when the SysConfig is missing, 0, or non-positive. Mount
 * once on the POS shell so it lives for the whole authenticated UI.
 */
export function useInactivityLogout() {
  const navigate = useNavigate()
  const { data: config } = usePOSConfig()
  const timeoutSec = config?.posLogoutTimeout ?? 0
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!timeoutSec || timeoutSec <= 0) return

    const onTimeout = () => {
      const { clerkToken, clearClerkKeepRegister } = useSessionStore.getState()
      if (!clerkToken) return
      // Intentionally leave uiStore (activeModal) and posStore
      // (currentReceiptId, cart, serverTotals) alone here.
      //
      // Two scenarios make cleanup risky:
      //   1. Terminal payment in flight — clearing currentReceiptId
      //      would orphan a real $ transaction. The customer eventually
      //      taps, the BE marks the receipt paid, but the cashier
      //      returns to no local trace and only finds it at end-of-day
      //      reconciliation.
      //   2. Modal sitting on the 'complete' step (post-payment, print
      //      affordance visible) — closing the modal here strips the
      //      cashier's path to print on return.
      //
      // Recovery for the originally-reported "stuck on method screen"
      // bug is handled in CheckoutModal: a mount-time effect auto-
      // advances step → 'complete' when the receipt is fully paid.
      // The Void button also has a pre-flight that detaches a paid
      // receipt locally without hitting the BE.
      // clearClerkKeepRegister (not clearClerkAuth): preserving the
      // register binding lets /login render the PIN page DIRECTLY --
      // clearClerkAuth nulls selectedRegister, which bounced this
      // navigation through /select-register, contradicting the "route
      // to /login (PIN view)" intent documented above.
      clearClerkKeepRegister()
      navigate({ to: '/login' })
    }

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(onTimeout, timeoutSec * 1000)
    }

    reset()
    // Capture phase on `document` so other listeners (e.g. the barcode
    // scanner's accepted-scan stopPropagation) can't suppress the reset.
    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, reset, { capture: true, passive: true }),
    )

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      ACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, reset, { capture: true }),
      )
    }
  }, [timeoutSec, navigate])
}
